import classNames from 'classnames';
import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import { Table, TableProps } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

interface AdvanceTableProps {
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  tableProps?: TableProps;
  hasFooter?: boolean;
  highlightedRow?: string;
  // highlightedKey?: string;
  loading?: boolean;
}

const AdvanceTable = ({
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
  hasFooter,
  highlightedRow,
  // highlightedKey,
  loading = false
}: AdvanceTableProps) => {
  const table = useAdvanceTableContext();
  const { getRowModel, getFlatHeaders, getFooterGroups } = table;
  const animationRef = useRef<HTMLTableSectionElement>(null);
  const [isAnimationDone, setIsAnimationDone] = useState(false);

  useEffect(() => {
    if (animationRef.current && !loading) {
      gsap.fromTo(
        animationRef.current.querySelectorAll('tr'),
        {
          opacity: 0,
          y: 0
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: 'power1.in',
          // Callback function to be executed when the animation is complete
          onComplete: () => {
            setIsAnimationDone(true);
            if (animationRef.current) {
              gsap.set(animationRef.current.querySelectorAll('tr'), {
                clearProps: 'transform'
              });
            }
            // Clear specified CSS properties after animation is done
          }
        }
      );
    }
  }, [getRowModel, loading]);

  return (
    <div className="scrollbar ms-n1 ps-1" style={{ minHeight: '350px' }}>
      <Table {...tableProps}>
        <thead className={headerClassName}>
          <tr>
            {getFlatHeaders().map(header => {
              return (
                <th
                  key={header.id}
                  {...header.column.columnDef.meta?.headerProps}
                  className={classNames(
                    header.column.columnDef.meta?.headerProps?.className,
                    {
                      sort: header.column.getCanSort(),
                      desc: header.column.getIsSorted() === 'desc',
                      asc: header.column.getIsSorted() === 'asc'
                    }
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={bodyClassName} id="animate-tr" ref={animationRef}>
          {getRowModel().rows?.length > 0 ? (
            <>
              {getRowModel().rows.map((row, i) => {
                return (
                  <tr
                    key={row.id}
                    style={{ opacity: !isAnimationDone ? 0 : 1 }}
                    className={`${rowClassName} ${
                      i === 0 && highlightedRow ? 'bg-primary-100' : ''
                    }`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        {...cell.column.columnDef.meta?.cellProps}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </>
          ) : (
            <tr>
              {!loading ? (
                <td colSpan={getFlatHeaders()?.length || 1}>
                  <div className="text-danger text-center">
                    No records found
                  </div>
                </td>
              ) : null}
            </tr>
          )}
        </tbody>
        {hasFooter && (
          <tfoot>
            {getFooterGroups().map(footerGroup => (
              <tr key={footerGroup.id} className="border-0">
                {footerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      {...header.column.columnDef.meta?.footerProps}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </Table>
    </div>
  );
};

export default AdvanceTable;
